// Client-side scorer for the unified "smart søk" modal (GlobalSearchModal).
//
// The backend arms (tags / notes / action-points) each pre-filter their own
// rows server-side; this module re-scores the merged set client-side so a
// single ranking, grouping and match-highlight rule applies across every
// type — AND so records visible on the page the operator opened search from
// float to the top (the context boost).
//
// Ranking mirrors the design prototype (Søkemodal): a per-field score
// (title ×10, keywords ×5, context ×3) summed across query terms, plus a
// +6 boost for on-view records and +1.2 for overdue follow-ups. It is built
// on the SHARED fuzzy primitives (fuzzyNormalize + fuzzyLevenshtein + the
// pinned fuzzyThreshold table) so it can't drift from the rest of search —
// see docs/search.md. Pure + Vue-free so it unit-tests directly.

import { fuzzyLevenshtein, fuzzyNormalize, fuzzyThreshold } from '@/utils/fuzzySearch'

// The seven result types from the design, plus `dokument` (kept from the
// pre-redesign modal so we don't regress document search — the mock omits
// it). Ordering is the design's TYPE_ORDER; documents trail.
export type SearchType =
  | 'mote'
  | 'oppfolging'
  | 'kunde'
  | 'notat'
  | 'melding'
  | 'agenda'
  | 'tjeneste'
  | 'radgiver'
  | 'tagg'
  | 'dokument'

export const TYPE_ORDER: Record<SearchType, number> = {
  mote: 0,
  oppfolging: 1,
  kunde: 2,
  notat: 3,
  melding: 4,
  agenda: 5,
  tjeneste: 6,
  radgiver: 7,
  tagg: 8,
  dokument: 9,
}

// Field weights — title dominates, keywords (synonyms/derived terms) next,
// context (the mono subline) least. Matches the prototype's [10, 5, 3].
const W_TITLE = 10
const W_KEYWORDS = 5
const W_CONTEXT = 3

// Score bumps. onView is the load-bearing "context aware" signal: a hit for
// something already rendered on the current page ranks above an equally-good
// off-page hit. forfalt nudges overdue follow-ups up a hair.
const BOOST_ON_VIEW = 6
const BOOST_FORFALT = 1.2

// A record the scorer can rank. Everything but `id`/`type`/`title` is
// optional — a tag hit has no keywords, a service hit no forfalt, etc.
//
// `pinned` marks a hit the server already vouched for (a backend result):
// it is kept even when the client-side fields don't reproduce the match —
// a note matched on its ENCRYPTED body, say, which the client never sees.
// Pinned hits still get scored + boosted for ordering; they just can't be
// dropped. Client-only hits (services/team/tags) leave it false so the
// full catalog is filtered down to real matches.
export interface ScorableHit<T = unknown> {
  id: string
  type: SearchType
  title: string
  keywords?: string
  ctx?: string
  forfalt?: boolean
  onView?: boolean
  pinned?: boolean
  payload: T
}

export interface ScoredHit<T = unknown> {
  hit: ScorableHit<T>
  score: number
}

export interface HitGroup<T = unknown> {
  type: SearchType
  hits: Array<ScorableHit<T>>
}

// qterms splits a raw query into normalised terms. `#` survives so a tag
// query like "#skulder" tokenises as one term including the hash — matching
// the design, where tag titles carry the leading hash.
export function qterms(query: string): string[] {
  return fuzzyNormalize(query)
    .split(/[^a-z0-9#]+/)
    .filter((t) => t.length > 0)
}

function tokensOf(normalised: string): string[] {
  return normalised.match(/[a-z0-9#]+/g) ?? []
}

// fieldScore ranks one query term against one normalised field. The ladder
// (exact token > prefix > substring > fuzzy) mirrors the prototype; the
// fuzzy rung honours OUR pinned threshold table rather than the prototype's
// ad-hoc "≤4 ? 1 : 2" so the modal can't diverge from list-filter search.
export function fieldScore(term: string, normalised: string, tokens: string[]): number {
  let best = 0
  for (const tok of tokens) {
    if (tok === term) best = Math.max(best, 1.25)
    else if (tok.startsWith(term)) best = Math.max(best, 1.0)
  }
  if (best >= 1.0) return best
  if (normalised.includes(term)) best = Math.max(best, 0.7)
  if (best >= 0.7) return best
  const allowed = fuzzyThreshold(term.length)
  if (allowed === 0) return best
  for (const tok of tokens) {
    if (Math.abs(tok.length - term.length) > allowed) continue
    const d = fuzzyLevenshtein(term, tok)
    if (d <= allowed) best = Math.max(best, 0.62 - 0.14 * (d - 1))
  }
  return best
}

interface IndexedFields {
  norm: string
  tokens: string[]
  weight: number
}

function indexHit(hit: ScorableHit): IndexedFields[] {
  const field = (t: string | undefined, weight: number): IndexedFields => {
    const norm = fuzzyNormalize(t ?? '')
    return { norm, tokens: tokensOf(norm), weight }
  }
  return [field(hit.title, W_TITLE), field(hit.keywords, W_KEYWORDS), field(hit.ctx, W_CONTEXT)]
}

function boosts(hit: ScorableHit): number {
  return (hit.onView ? BOOST_ON_VIEW : 0) + (hit.forfalt ? BOOST_FORFALT : 0)
}

// matchScore ranks the hit's own fields against the terms, or null when any
// term matches nothing (AND across terms). Boost-free — the caller adds
// boosts so a pinned-but-unmatched hit can still be scored on boosts alone.
function matchScore(hit: ScorableHit, terms: string[]): number | null {
  if (terms.length === 0) return null
  const fields = indexHit(hit)
  let total = 0
  for (const term of terms) {
    let best = 0
    for (const f of fields) {
      const s = fieldScore(term, f.norm, f.tokens)
      if (s > 0) best = Math.max(best, s * f.weight)
    }
    if (best === 0) return null
    total += best
  }
  return total
}

// scoreHit returns the total relevance (match + boosts), or null when any
// term fails to match every field. Used directly by unit tests and by the
// non-pinned path in rankAndGroup.
export function scoreHit(hit: ScorableHit, terms: string[]): number | null {
  const m = matchScore(hit, terms)
  if (m === null) return null
  return m + boosts(hit)
}

// rankAndGroup scores every hit, drops non-matches, sorts by score (type
// order breaks ties), then buckets by type. Group order follows the group's
// best hit, so the type with the strongest single match leads — the design's
// behaviour, which keeps "camilla" surfacing the customer above her meetings
// only when the customer scores higher.
export function rankAndGroup<T>(hits: Array<ScorableHit<T>>, query: string): Array<HitGroup<T>> {
  const terms = qterms(query)
  if (terms.length === 0) return []
  const scored: Array<ScoredHit<T>> = []
  for (const hit of hits) {
    const base = matchScore(hit, terms)
    // Drop only client-side hits that matched nothing. A pinned (server-
    // vouched) hit stays, ranked on boosts alone when its client fields
    // don't reproduce the server's match.
    if (base === null && !hit.pinned) continue
    scored.push({ hit, score: (base ?? 0) + boosts(hit) })
  }
  scored.sort((a, b) => b.score - a.score || TYPE_ORDER[a.hit.type] - TYPE_ORDER[b.hit.type])
  const byType = new Map<SearchType, Array<ScoredHit<T>>>()
  for (const s of scored) {
    const arr = byType.get(s.hit.type)
    if (arr) arr.push(s)
    else byType.set(s.hit.type, [s])
  }
  return [...byType.entries()]
    .sort(([ta, a], [tb, b]) => b[0].score - a[0].score || TYPE_ORDER[ta] - TYPE_ORDER[tb])
    .map(([type, arr]) => ({ type, hits: arr.map((s) => s.hit) }))
}

// ── Match highlighting ──────────────────────────────────────────────
// Splits `text` into {text, match} segments so the modal renders matched
// spans in <mark> without ever using v-html. Marks substring hits AND fuzzy
// token hits (so a typo'd query still visibly lights up the word it matched).
export interface HighlightSegment {
  text: string
  match: boolean
}

export function highlightSegments(text: string, terms: string[]): HighlightSegment[] {
  if (!text) return [{ text: '', match: false }]
  if (terms.length === 0) return [{ text, match: false }]
  // Fold the text the same way qterms folded the query, but keep an index map
  // from each folded offset back to the original code unit it came from. A fold
  // that changes length (æ → ae) or drops chars (combining marks) then no longer
  // desyncs the highlight mask from the original string — the old code bailed to
  // a naive lowercase substring search that missed æ/ø/å titles entirely (a hit
  // like "Kjærstad" for "kjærstad" rendered with no highlight, #2403).
  const { folded, map } = foldWithMap(text)
  const mask = new Array(text.length).fill(false)
  // Project a [start, start+len) span in folded space onto the original text.
  // The exclusive end follows the source index of the folded char just past the
  // span (or the string end), so chars dropped by folding inside the span (e.g.
  // combining marks) and the tail of a multi-char fold are both covered.
  const mark = (start: number, len: number) => {
    if (len === 0) return
    const from = map[start]
    const end = start + len
    const to = end < map.length ? map[end] : text.length
    for (let k = from; k < to; k++) mask[k] = true
  }
  for (const term of terms) {
    let i = folded.indexOf(term)
    while (i !== -1) {
      mark(i, term.length)
      i = folded.indexOf(term, i + term.length)
    }
  }
  const re = /[a-z0-9#]+/g
  let m = re.exec(folded)
  while (m !== null) {
    const tok = m[0]
    const start = m.index
    for (const term of terms) {
      const allowed = fuzzyThreshold(term.length)
      if (
        Math.abs(tok.length - term.length) <= allowed &&
        (tok.startsWith(term) || (allowed > 0 && fuzzyLevenshtein(term, tok) <= allowed))
      ) {
        mark(start, tok.length)
      }
    }
    m = re.exec(folded)
  }
  return coalesce(text, mask)
}

// foldWithMap applies the fuzzyNormalize fold character by character, recording
// for each folded code unit the index of the original code unit that produced
// it. Multi-character folds (æ → ae) repeat the source index; stripped
// characters (combining marks) contribute no folded output. `folded` equals
// fuzzyNormalize(text) for the inputs we handle, so folded-space matching stays
// identical to the query terms qterms produced — the map only exists to project
// matches back onto the original when the fold changed the string length.
function foldWithMap(text: string): { folded: string; map: number[] } {
  let folded = ''
  const map: number[] = []
  for (let i = 0; i < text.length; i++) {
    const piece = fuzzyNormalize(text[i])
    folded += piece
    for (let k = 0; k < piece.length; k++) map.push(i)
  }
  return { folded, map }
}

function coalesce(text: string, mask: boolean[]): HighlightSegment[] {
  const parts: HighlightSegment[] = []
  let cur = text[0]
  let curHit = mask[0]
  for (let i = 1; i < text.length; i++) {
    if (mask[i] === curHit) cur += text[i]
    else {
      parts.push({ text: cur, match: curHit })
      cur = text[i]
      curHit = mask[i]
    }
  }
  parts.push({ text: cur, match: curHit })
  return parts
}
